import { useEffect } from 'react';
import styled from 'styled-components';

import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { StatePlaceholder } from '../../../cdl/StatePlaceholder/StatePlaceholder';
import { Text } from '../../../cdl/Text/Text';
import { IntelligenceEventType } from '../../../common/api/clients/intelligence.api';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { IconAlertTriangle } from '../../../common/icons/cdl/AlertTriangle';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { IconSparkle } from '../../../common/icons/cdl/Sparkle';
import { LoadingBar } from '../../../common/LoadingBar/LoadingBar';
import { Box } from '../../../common/ui/Box';
import { IntelligenceReasoning } from '../../create-enquiry/components/intelligence/IntelligenceReasoning';
import { SuggestionProgressIndicator } from '../../create-enquiry/components/SuggestionProgressIndicator';
import { usePortSuggestionsStream } from '../../create-enquiry/hooks/usePortSuggestionsStream';

const Container = styled(Box).attrs({
    overflowY: 'auto',
    paddingX: 24,
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
})``;

interface SuggestionDrawerContentProps {
    query: ReturnType<typeof usePortSuggestionsStream>;
    isDraftUpdatePending: boolean;
}

export const SuggestionDrawerContent = ({ query, isDraftUpdatePending }: SuggestionDrawerContentProps) => {
    useEffect(() => {
        if (!query.isLoading && !query.isError && !query.finalResult && !query.events.length && !isDraftUpdatePending) {
            query.startStream();
        }
    }, [query, isDraftUpdatePending]);

    const latestLLMEvent = query.events.filter((event) => event.event === IntelligenceEventType.LLM_UPDATE).at(-1);

    if (query.isLoading || isDraftUpdatePending) {
        return (
            <Container>
                <Box
                    display="grid"
                    gridTemplateColumns="20px auto"
                    alignItems="center"
                    gap={8}
                    padding={16}
                    color="special.foreground"
                    borderRadius={8}
                    border="1px solid"
                    borderColor="border.subtle"
                    backgroundColor="background.subtle"
                    marginBottom={8}
                >
                    <IconSparkle />
                    <Text variant="small" fontWeight={600}>
                        {translate('closelinkInsights.reviewInProgress')}
                        {Characters.HORIZONTAL_ELLIPSIS}
                    </Text>
                    <Box gridColumn="1/3">
                        <LoadingBar color="special.emphasis" />
                    </Box>
                </Box>

                <SuggestionProgressIndicator events={query.events} />

                {latestLLMEvent ? <IntelligenceReasoning markdownReasoning={latestLLMEvent.data} /> : null}
            </Container>
        );
    }

    if (query.isError) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" marginTop="124px">
                    <StatePlaceholder
                        title={translate('global.genericError.title')}
                        description={translate('global.genericError.description')}
                        Icon={IconAlertTriangle}
                        action={
                            <Button onClick={() => query.startStream()}>
                                {translate('closelinkInsights.tryAgain')}
                            </Button>
                        }
                    />
                </Box>
            </Container>
        );
    }

    if (query.events.length && !query.isLoading && !query.isSuccess) {
        const errorMessage = query.finalResult?.message || translate('closelinkInsights.noSuitablePort');
        const errorDescription = query.finalResult?.message
            ? undefined // Use the detailed message as heading, no additional description needed
            : translate('closelinkInsights.noSuitablePort.description');

        return (
            <Container>
                <Callout variant="accent" heading={errorMessage} description={errorDescription} Icon={IconInfoCircle} />
            </Container>
        );
    }

    return (
        <Container>
            <IntelligenceReasoning markdownReasoning={query.finalResult?.detailedMarkdownMessage} />
        </Container>
    );
};
